import React, { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import {
  Container,
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  MenuItem,
  InputAdornment,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import {
  QrCode as QrCodeIcon,
  Add as AddIcon,
  FileDownload as FileDownloadIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Clear as ClearIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Timelapse as TimelapseIcon,
  HourglassEmpty as HourglassEmptyIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';

import useAsistenciaLaboral from './hooks/useAsistenciaLaboral';
import AsistenciaLaboralStats from './components/AsistenciaLaboralStats';
import ModalTotemQR from './components/ModalTotemQR';
import ModalAsistenciaManual from './components/ModalAsistenciaManual';
import {
  agruparAsistenciasPorJornada,
  formatearFechaVisual,
} from './utils/asistenciaLaboralUtils';

export default function GestionAsistenciaLaboral() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const {
    asistencias,
    stats,
    loading,
    loadingStats,
    filters,
    setFilters,
    fetchAsistencias,
    fetchStats,
    handleCreateManual,
    handleDelete,
    handleDeleteMultiple,
    exportToExcel,
  } = useAsistenciaLaboral();

  const [showTotemModal, setShowTotemModal] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [estadoJornadaFiltro, setEstadoJornadaFiltro] = useState('all');

  // Agrupamiento reactivo de registros individuales por trabajador y por fecha
  const jornadasAgrupadas = useMemo(() => {
    return agruparAsistenciasPorJornada(asistencias);
  }, [asistencias]);

  // Filtrado local por Estado de Jornada (Todas / Completas / En Curso / Inconsistentes)
  const jornadasFiltradas = useMemo(() => {
    if (estadoJornadaFiltro === 'all') return jornadasAgrupadas;
    return jornadasAgrupadas.filter((jornada) => jornada.estado === estadoJornadaFiltro);
  }, [jornadasAgrupadas, estadoJornadaFiltro]);

  const handleDateQuickSelect = (mode) => {
    const today = dayjs().format('YYYY-MM-DD');
    if (mode === 'today') {
      setFilters((prev) => ({ ...prev, fecha: today, fecha_desde: '', fecha_hasta: '' }));
    } else if (mode === 'yesterday') {
      const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
      setFilters((prev) => ({ ...prev, fecha: yesterday, fecha_desde: '', fecha_hasta: '' }));
    } else if (mode === 'week') {
      const startOfWeek = dayjs().startOf('week').format('YYYY-MM-DD');
      setFilters((prev) => ({ ...prev, fecha: '', fecha_desde: startOfWeek, fecha_hasta: today }));
    } else if (mode === 'all') {
      setFilters((prev) => ({ ...prev, fecha: '', fecha_desde: '', fecha_hasta: '' }));
    }
  };

  const handleClearFilters = () => {
    const today = dayjs().format('YYYY-MM-DD');
    setEstadoJornadaFiltro('all');
    setFilters({
      fecha: today,
      fecha_desde: '',
      fecha_hasta: '',
      tipo_registro: 'all',
      search: '',
    });
  };

  const confirmDeleteJornada = (row) => {
    // Caso 1: Solo tiene Entrada
    if (row.entrada && !row.salida) {
      Swal.fire({
        title: '¿Eliminar registro de Entrada?',
        text: `Se eliminará la entrada de ${row.nombre_completo} (CUIL: ${row.cuil}) del día ${formatearFechaVisual(row.fecha)} a las ${row.entrada.hora.substring(0, 5)} hs.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: theme.palette.error.main,
        cancelButtonColor: theme.palette.grey[500] || '#64748B',
        confirmButtonText: 'Sí, eliminar entrada',
        cancelButtonText: 'Cancelar',
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await handleDelete(row.entrada.id);
            setSnackbar({ open: true, message: 'Registro de entrada eliminado.', severity: 'success' });
          } catch (err) {
            setSnackbar({ open: true, message: err.message || 'Error al eliminar', severity: 'error' });
          }
        }
      });
      return;
    }

    // Caso 2: Solo tiene Salida
    if (!row.entrada && row.salida) {
      Swal.fire({
        title: '¿Eliminar registro de Salida?',
        text: `Se eliminará la salida de ${row.nombre_completo} (CUIL: ${row.cuil}) del día ${formatearFechaVisual(row.fecha)} a las ${row.salida.hora.substring(0, 5)} hs.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: theme.palette.error.main,
        cancelButtonColor: theme.palette.grey[500] || '#64748B',
        confirmButtonText: 'Sí, eliminar salida',
        cancelButtonText: 'Cancelar',
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await handleDelete(row.salida.id);
            setSnackbar({ open: true, message: 'Registro de salida eliminado.', severity: 'success' });
          } catch (err) {
            setSnackbar({ open: true, message: err.message || 'Error al eliminar', severity: 'error' });
          }
        }
      });
      return;
    }

    // Caso 3: Jornada Completa (tiene Entrada y Salida)
    Swal.fire({
      title: 'Gestión de Jornada Laboral',
      html: `
        <div style="text-align: left; font-size: 0.95rem; line-height: 1.5; color: #334155;">
          <p style="margin-bottom: 6px;"><strong>Trabajador:</strong> ${row.nombre_completo} (CUIL: ${row.cuil})</p>
          <p style="margin-bottom: 6px;">📅 <strong>Fecha:</strong> ${formatearFechaVisual(row.fecha)}</p>
          <p style="margin-bottom: 6px;">🟢 <strong>Entrada:</strong> ${row.entrada.hora.substring(0, 5)} hs | 🟠 <strong>Salida:</strong> ${row.salida.hora.substring(0, 5)} hs</p>
          <p style="margin-bottom: 12px;">⏱️ <strong>Total trabajado:</strong> ${row.totalHorasTexto}</p>
          <hr style="border: 0; border-top: 1px solid #e2e8f0; margin: 12px 0;" />
          <p style="font-weight: 600; color: #0f172a; text-align: center;">¿Qué acción deseas realizar?</p>
        </div>
      `,
      icon: 'question',
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: 'Eliminar Salida (Reabrir)',
      denyButtonText: 'Eliminar Jornada Completa',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#F59E0B',
      denyButtonColor: '#EF4444',
      cancelButtonColor: '#94A3B8',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await handleDelete(row.salida.id);
          setSnackbar({ open: true, message: 'Salida eliminada. La jornada quedó abierta (en curso).', severity: 'success' });
        } catch (err) {
          setSnackbar({ open: true, message: err.message || 'Error al eliminar', severity: 'error' });
        }
      } else if (result.isDenied) {
        try {
          await handleDeleteMultiple([row.entrada.id, row.salida.id]);
          setSnackbar({ open: true, message: 'Jornada completa eliminada.', severity: 'success' });
        } catch (err) {
          setSnackbar({ open: true, message: err.message || 'Error al eliminar', severity: 'error' });
        }
      }
    });
  };

  const handleManualSave = async (data) => {
    await handleCreateManual(data);
    setSnackbar({ open: true, message: 'Asistencia registrada con éxito.', severity: 'success' });
  };

  const columns = [
    {
      field: 'fecha',
      headerName: 'Fecha',
      width: 110,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {formatearFechaVisual(params.value)}
        </Typography>
      ),
    },
    {
      field: 'nombre_completo',
      headerName: 'Trabajador',
      flex: 1.2,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'secondary.main', lineHeight: 1.2 }}>
            {params.value}
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', display: 'block', mt: 0.2 }}>
            CUIL: {params.row.cuil}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'entrada',
      headerName: 'Entrada',
      width: 145,
      valueGetter: (_value, row) => row.entrada?.hora || '',
      renderCell: (params) => {
        const entrada = params.row.entrada;
        if (!entrada) {
          return (
            <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
              —
            </Typography>
          );
        }
        return (
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'success.main' }}>
              {entrada.hora.substring(0, 5)} hs
            </Typography>
            <Chip
              size="small"
              label={entrada.origen || 'QR'}
              sx={{
                height: 18,
                fontSize: '0.62rem',
                fontWeight: 600,
                bgcolor: entrada.origen === 'MANUAL' ? 'rgba(124, 58, 237, 0.08)' : 'rgba(0, 154, 218, 0.08)',
                color: entrada.origen === 'MANUAL' ? '#7C3AED' : 'primary.main',
              }}
            />
          </Stack>
        );
      },
    },
    {
      field: 'salida',
      headerName: 'Salida',
      width: 155,
      valueGetter: (_value, row) =>
        row.salida?.hora ||
        (row.estado === 'EN_CURSO' ? 'Pendiente' : row.estado === 'SIN_SALIDA' ? 'Sin salida' : ''),
      renderCell: (params) => {
        const salida = params.row.salida;
        if (!salida) {
          if (params.row.estado === 'EN_CURSO') {
            return (
              <Chip
                size="small"
                icon={<TimelapseIcon sx={{ fontSize: '14px !important' }} />}
                label="Pendiente"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  height: 22,
                  bgcolor: '#FFFBEB',
                  color: '#B45309',
                  border: '1px solid #FCD34D',
                  '& .MuiChip-icon': { color: '#B45309' },
                }}
              />
            );
          }
          if (params.row.estado === 'SIN_SALIDA') {
            return (
              <Chip
                size="small"
                icon={<WarningIcon sx={{ fontSize: '13px !important' }} />}
                label="Sin salida"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.68rem',
                  height: 22,
                  bgcolor: '#FEF2F2',
                  color: '#B91C1C',
                  border: '1px solid #FCA5A5',
                  '& .MuiChip-icon': { color: '#B91C1C' },
                }}
              />
            );
          }
          return (
            <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
              —
            </Typography>
          );
        }
        return (
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'warning.main' }}>
              {salida.hora.substring(0, 5)} hs
            </Typography>
            <Chip
              size="small"
              label={salida.origen || 'QR'}
              sx={{
                height: 18,
                fontSize: '0.62rem',
                fontWeight: 600,
                bgcolor: salida.origen === 'MANUAL' ? 'rgba(124, 58, 237, 0.08)' : 'rgba(0, 154, 218, 0.08)',
                color: salida.origen === 'MANUAL' ? '#7C3AED' : 'primary.main',
              }}
            />
          </Stack>
        );
      },
    },
    {
      field: 'totalHorasTexto',
      headerName: 'Total Horas',
      width: 150,
      renderCell: (params) => {
        const { estado, totalHorasTexto } = params.row;
        if (estado === 'COMPLETO') {
          return (
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <AccessTimeIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                {totalHorasTexto}
              </Typography>
            </Stack>
          );
        }
        if (estado === 'EN_CURSO') {
          return (
            <Chip
              size="small"
              icon={<HourglassEmptyIcon sx={{ fontSize: '13px !important' }} />}
              label="En curso"
              sx={{
                fontWeight: 600,
                fontSize: '0.68rem',
                height: 22,
                bgcolor: 'rgba(0, 154, 218, 0.1)',
                color: 'primary.main',
                border: '1px dashed',
                borderColor: 'primary.main',
                '& .MuiChip-icon': { color: 'primary.main' },
              }}
            />
          );
        }
        if (estado === 'SIN_SALIDA') {
          return (
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem', fontStyle: 'italic' }}>
              —
            </Typography>
          );
        }
        return (
          <Typography variant="body2" sx={{ color: 'error.main', fontSize: '0.75rem', fontWeight: 600 }}>
            {totalHorasTexto}
          </Typography>
        );
      },
    },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 135,
      renderCell: (params) => {
        const estado = params.value;
        if (estado === 'COMPLETO') {
          return (
            <Chip
              size="small"
              icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
              label="Completa"
              sx={{
                fontWeight: 700,
                fontSize: '0.7rem',
                height: 22,
                bgcolor: 'rgba(50, 164, 48, 0.12)',
                color: 'success.main',
                border: '1px solid',
                borderColor: 'success.main',
                '& .MuiChip-icon': { color: 'success.main' },
              }}
            />
          );
        }
        if (estado === 'EN_CURSO') {
          return (
            <Chip
              size="small"
              icon={<TimelapseIcon sx={{ fontSize: '14px !important' }} />}
              label="En curso"
              sx={{
                fontWeight: 700,
                fontSize: '0.7rem',
                height: 22,
                bgcolor: 'rgba(238, 152, 0, 0.12)',
                color: 'warning.main',
                border: '1px solid',
                borderColor: 'warning.main',
                '& .MuiChip-icon': { color: 'warning.main' },
              }}
            />
          );
        }
        if (estado === 'SIN_SALIDA') {
          return (
            <Chip
              size="small"
              icon={<WarningIcon sx={{ fontSize: '13px !important' }} />}
              label="Sin salida"
              sx={{
                fontWeight: 700,
                fontSize: '0.7rem',
                height: 22,
                bgcolor: '#FEF2F2',
                color: '#B91C1C',
                border: '1px solid #FCA5A5',
                '& .MuiChip-icon': { color: '#B91C1C' },
              }}
            />
          );
        }
        return (
          <Chip
            size="small"
            label="Inconsistente"
            sx={{
              fontWeight: 700,
              fontSize: '0.7rem',
              height: 22,
              bgcolor: 'rgba(226, 70, 76, 0.12)',
              color: 'error.main',
              border: '1px solid',
              borderColor: 'error.main',
            }}
          />
        );
      },
    },
    {
      field: 'acciones',
      headerName: 'Acciones',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Gestionar / Eliminar registros">
          <IconButton size="small" onClick={() => confirmDeleteJornada(params.row)} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1.5, sm: 3 } }}>
      {/* Header Principal */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: { xs: 2.5, sm: 3.5 },
          borderRadius: '20px',
          bgcolor: 'common.white',
          border: '1px solid',
          borderColor: 'grey.300',
          boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={{ xs: 2, md: 2 }}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 'bold',
                  color: 'secondary.main',
                  fontSize: { xs: '1.4rem', sm: '1.85rem', md: '2.1rem' },
                }}
              >
                Registro de Asistencia Laboral
              </Typography>
              <Chip
                label="En vivo"
                size="small"
                sx={{
                  bgcolor: 'rgba(50, 164, 48, 0.12)',
                  color: 'success.main',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  border: '1px solid',
                  borderColor: 'success.main',
                  height: 22,
                  '& .MuiChip-label': { px: 1 },
                  display: { xs: 'none', sm: 'inline-flex' },
                }}
              />
            </Stack>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.82rem', sm: '0.9rem' } }}>
              Monitoreo y gestión de jornadas de trabajo por código QR o registro manual con cálculo automático de horas.
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} width={{ xs: '100%', md: 'auto' }} flexWrap="wrap">
            <Button
              variant="contained"
              fullWidth={isMobile}
              startIcon={<QrCodeIcon />}
              onClick={() => setShowTotemModal(true)}
              sx={{
                fontWeight: 600,
                borderRadius: '12px',
                bgcolor: 'secondary.main',
                py: { xs: 1, sm: 1.1 },
                px: 2,
                boxShadow: '0 4px 14px rgba(0, 69, 130, 0.25)',
                '&:hover': { bgcolor: 'custom.CBA_Blue_Darker' },
              }}
            >
              Mostrar QR
            </Button>

            <Button
              variant="contained"
              fullWidth={isMobile}
              startIcon={<AddIcon />}
              onClick={() => setShowManualModal(true)}
              sx={{
                fontWeight: 600,
                borderRadius: '12px',
                bgcolor: 'success.main',
                color: 'common.white',
                py: { xs: 1, sm: 1.1 },
                px: 2,
                '&:hover': { bgcolor: '#288826' },
                boxShadow: '0 4px 14px rgba(50, 164, 48, 0.25)',
              }}
            >
              Registro Manual
            </Button>

            <Button
              variant="outlined"
              fullWidth={isMobile}
              startIcon={<FileDownloadIcon />}
              onClick={() => exportToExcel(jornadasFiltradas)}
              disabled={jornadasFiltradas.length === 0}
              sx={{
                fontWeight: 600,
                borderRadius: '12px',
                borderColor: 'primary.main',
                color: 'primary.main',
              }}
            >
              Exportar Excel
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Tarjetas de Estadísticas / KPIs */}
      <AsistenciaLaboralStats stats={stats} loading={loadingStats} />

      {/* Barra de Filtros */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.75, sm: 2.5 },
          mb: 3,
          borderRadius: '16px',
          bgcolor: 'common.white',
          border: '1px solid',
          borderColor: 'grey.300',
        }}
      >
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'stretch', lg: 'center' }} justifyContent="space-between">
          {/* Botones de fechas rápidas */}
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              size="small"
              variant={filters.fecha === dayjs().format('YYYY-MM-DD') && !filters.fecha_desde ? 'contained' : 'outlined'}
              onClick={() => handleDateQuickSelect('today')}
              sx={{
                borderRadius: '10px',
                fontSize: { xs: '0.72rem', sm: '0.8rem' },
                bgcolor: filters.fecha === dayjs().format('YYYY-MM-DD') && !filters.fecha_desde ? 'secondary.main' : 'transparent',
              }}
            >
              Hoy
            </Button>
            <Button
              size="small"
              variant={filters.fecha === dayjs().subtract(1, 'day').format('YYYY-MM-DD') ? 'contained' : 'outlined'}
              onClick={() => handleDateQuickSelect('yesterday')}
              sx={{
                borderRadius: '10px',
                fontSize: { xs: '0.72rem', sm: '0.8rem' },
                bgcolor: filters.fecha === dayjs().subtract(1, 'day').format('YYYY-MM-DD') ? 'secondary.main' : 'transparent',
              }}
            >
              Ayer
            </Button>
            <Button
              size="small"
              variant={Boolean(filters.fecha_desde)}
              onClick={() => handleDateQuickSelect('week')}
              sx={{
                borderRadius: '10px',
                fontSize: { xs: '0.72rem', sm: '0.8rem' },
                bgcolor: Boolean(filters.fecha_desde) ? 'secondary.main' : 'transparent',
              }}
            >
              Esta Semana
            </Button>
            <Button
              size="small"
              variant={!filters.fecha && !filters.fecha_desde ? 'contained' : 'outlined'}
              onClick={() => handleDateQuickSelect('all')}
              sx={{
                borderRadius: '10px',
                fontSize: { xs: '0.72rem', sm: '0.8rem' },
                bgcolor: !filters.fecha && !filters.fecha_desde ? 'secondary.main' : 'transparent',
              }}
            >
              Historial Completo
            </Button>
          </Stack>

          {/* Filtros específicos: Fecha, Estado de Jornada y Búsqueda */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center" width={{ xs: '100%', lg: 'auto' }}>
            <TextField
              type="date"
              size="small"
              fullWidth={isMobile}
              label="Fecha puntual"
              value={filters.fecha}
              onChange={(e) => setFilters((prev) => ({ ...prev, fecha: e.target.value, fecha_desde: '', fecha_hasta: '' }))}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { xs: '100%', sm: 140 } }}
            />

            <TextField
              select
              size="small"
              fullWidth={isMobile}
              label="Estado de Jornada"
              value={estadoJornadaFiltro}
              onChange={(e) => setEstadoJornadaFiltro(e.target.value)}
              sx={{ minWidth: { xs: '100%', sm: 180 } }}
            >
              <MenuItem value="all">Todas ({jornadasAgrupadas.length})</MenuItem>
              <MenuItem value="COMPLETO">
                Jornadas Completas ({jornadasAgrupadas.filter((j) => j.estado === 'COMPLETO').length})
              </MenuItem>
              <MenuItem value="EN_CURSO">
                Jornadas En Curso ({jornadasAgrupadas.filter((j) => j.estado === 'EN_CURSO').length})
              </MenuItem>
              <MenuItem value="SIN_SALIDA">
                Sin Salida Registrada ({jornadasAgrupadas.filter((j) => j.estado === 'SIN_SALIDA').length})
              </MenuItem>
              <MenuItem value="INCONSISTENTE">
                Inconsistentes ({jornadasAgrupadas.filter((j) => j.estado === 'INCONSISTENTE').length})
              </MenuItem>
            </TextField>

            <TextField
              size="small"
              fullWidth={isMobile}
              placeholder="Buscar CUIL o Nombre..."
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: { xs: '100%', sm: 200 } }}
            />

            <Stack direction="row" spacing={1} justifyContent="flex-end" width={{ xs: '100%', sm: 'auto' }}>
              <Tooltip title="Limpiar filtros">
                <IconButton onClick={handleClearFilters} sx={{ color: 'text.secondary' }}>
                  <ClearIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Actualizar datos">
                <IconButton onClick={() => { fetchAsistencias(); fetchStats(); }} sx={{ color: 'secondary.main' }}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      {/* Tabla DataGrid de Jornadas */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '18px',
          overflow: 'hidden',
          bgcolor: 'common.white',
          border: '1px solid',
          borderColor: 'grey.300',
          boxShadow: '0 6px 24px rgba(0,0,0,0.04)',
        }}
      >
        <Box sx={{ height: { xs: 450, sm: 550, md: 600 }, width: '100%' }}>
          <DataGrid
            rows={jornadasFiltradas}
            columns={columns}
            loading={loading}
            localeText={esES.components.MuiDataGrid.defaultProps.localeText}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            disableRowSelectionOnClick
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: 'grey.100',
                borderBottom: '2px solid',
                borderColor: 'grey.300',
                fontWeight: 700,
                color: 'text.primary',
              },
              '& .MuiDataGrid-row:hover': {
                bgcolor: 'grey.100',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid',
                borderColor: 'grey.200',
                display: 'flex',
                alignItems: 'center',
              },
            }}
          />
        </Box>
      </Paper>

      {/* Modales */}
      <ModalTotemQR open={showTotemModal} onClose={() => setShowTotemModal(false)} />
      <ModalAsistenciaManual
        open={showManualModal}
        onClose={() => setShowManualModal(false)}
        onSave={handleManualSave}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: '12px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
